'use client';

import { useState } from 'react';
import { AppImage } from '@impacta/shared';
import { AppIcon } from '@impacta/shared';
import { FEATURE_TESTIMONIOS_ENABLED } from '@/lib/features';

const testimonials = [
  {
    id: 1,
    name: 'Catalina Vásquez',
    role: 'Diseñadora gráfica · Providencia',
    quote:
      'El taller de acuarela botánica fue exactamente lo que necesitaba. Ambiente acogedor, instructora increíble y salí con algo real en mis manos. Me inscribí en tres más.',
    rating: 5,
    image: '/assets/images/homepage/testimonial-1.png',
    alt: 'Catalina Vásquez, diseñadora gráfica y estudiante de actividades IMPACTA.red',
    workshop: 'Acuarela Botánica',
  },
  {
    id: 2,
    name: 'Felipe Contreras',
    role: 'Ingeniero civil · Las Condes',
    quote:
      'Nunca pensé que aprender fotografía analógica me iba a enganchar tanto. El proceso de revelar en cuarto oscuro es algo que simplemente no se puede aprender en YouTube.',
    rating: 5,
    image: '/assets/images/homepage/testimonial-2.png',
    alt: 'Felipe Contreras, ingeniero y estudiante de fotografía analógica',
    workshop: 'Fotografía Analógica',
  },
  {
    id: 3,
    name: 'Isadora Muñoz',
    role: 'Abogada · Ñuñoa',
    quote:
      'La calidad de los talleristas es notablemente superior a otras plataformas. Se nota que hay una curaduría real detrás. Ya regalé tres tarjetas de experiencia.',
    rating: 5,
    image: '/assets/images/homepage/testimonial-3.png',
    alt: 'Isadora Muñoz, abogada y usuaria frecuente de IMPACTA.red',
    workshop: 'Cerámica en Torno',
  },
  {
    id: 4,
    name: 'Rodrigo Herrera',
    role: 'Médico · Vitacura',
    quote:
      'El taller de fermentados fue revelador. Aprendí técnicas que aplico cada semana en casa. La instructora tiene un dominio del tema impresionante y sabe cómo transmitirlo.',
    rating: 5,
    image: '/assets/images/homepage/testimonial-4.png',
    alt: 'Rodrigo Herrera, médico y estudiante de actividades de cocina',
    workshop: 'Fermentados Caseros',
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c - 1 + testimonials?.length) % testimonials?.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials?.length);

  const t = testimonials?.[current];

  if (!FEATURE_TESTIMONIOS_ENABLED) return null;

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="section-label mb-2">Testimonios</p>
            <h2 className="section-title">Lo que dicen quienes ya vivieron la experiencia</h2>
          </div>
          {/* Navigation */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={prev}
              className="w-11 h-11 rounded-full border-2 border-[#E8E8E4] flex items-center justify-center text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-all duration-200"
              aria-label="Testimonio anterior"
            >
              <AppIcon name="ArrowLeftIcon" size={18} />
            </button>
            <button
              onClick={next}
              className="w-11 h-11 rounded-full flex items-center justify-center text-white transition-all duration-200"
              style={{ backgroundColor: '#00A19B' }}
              aria-label="Siguiente testimonio"
            >
              <AppIcon name="ArrowRightIcon" size={18} />
            </button>
          </div>
        </div>

        {/* Main testimonial */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Image */}
          <div className="lg:col-span-4">
            <div className="relative rounded-3xl overflow-hidden aspect-[3/4] max-w-sm">
              <AppImage
                src={t?.image}
                alt={t?.alt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 33vw"
              />

              {/* Workshop badge */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3">
                  <p className="text-xs text-[#6b6e68]">Taller cursado</p>
                  <p className="text-sm font-bold text-[#373A36]">{t?.workshop}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-8 lg:pt-8">
            {/* Stars */}
            <div className="flex gap-1 mb-6">
              {Array.from({ length: t?.rating })?.map((_, i) => (
                <AppIcon
                  key={i}
                  name="StarIcon"
                  size={20}
                  className="text-[#E0DF00]"
                  variant="solid"
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-2xl md:text-3xl font-semibold text-[#373A36] leading-snug mb-8">
              &ldquo;{t?.quote}&rdquo;
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                <AppImage
                  src={t?.image}
                  alt={t?.alt}
                  width={48}
                  height={48}
                  className="object-cover"
                />
              </div>
              <div>
                <p className="font-bold text-[#373A36]">{t?.name}</p>
                <p className="text-sm text-[#6b6e68]">{t?.role}</p>
              </div>
            </div>

            {/* Pagination dots */}
            <div className="flex gap-2 mt-10">
              {testimonials?.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current ? 'w-8 bg-[#00A19B]' : 'w-4 bg-[#E8E8E4]'
                  }`}
                  aria-label={`Ir al testimonio ${i + 1}`}
                />
              ))}
            </div>

            {/* Mobile navigation */}
            <div className="flex md:hidden gap-3 mt-6">
              <button
                onClick={prev}
                className="w-11 h-11 rounded-full border-2 border-[#E8E8E4] flex items-center justify-center text-[#6b6e68]"
                aria-label="Anterior"
              >
                <AppIcon name="ArrowLeftIcon" size={18} />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: '#00A19B' }}
                aria-label="Siguiente"
              >
                <AppIcon name="ArrowRightIcon" size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
